import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  unlabeledAriaProps,
} from "../../field-ui";
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";

interface TextAreaInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  validation?: (value: string) => boolean;
}

const TextAreaInput = forwardRef<FormFieldRef, TextAreaInputProps>(
  (props, ref) => {
    const shellRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const inputId = useId();
    const [value, setValue] = useState(props.initialValue ?? "");
    const [validationState, setValidationState] = useState(true);

    const commitValue = (next: string) => {
      setValue(next);
      reportFieldValue(props.fieldName, next, props.onValueChange);
    };

    useImperativeHandle(ref, () => ({
      setValidationState,
      value,
      fieldId: props.fieldId,
      fieldName: props.fieldName,
      scrollToElement: (options) => {
        shellRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          ...options,
        });
      },
      validate,
      clear,
    }));

    const clear = () => commitValue("");

    const validate = () => {
      if (!props.validation) return true;
      const result = props.validation(value);
      setValidationState(result);
      return result;
    };

    return (
      <FieldShell ref={shellRef} className={props.className}>
        <FieldLabel htmlFor={inputId} muted={props.labelMuted}>
          {props.placeholder}
        </FieldLabel>
        <FieldControl className="!h-auto min-h-[7.5rem]">
          <textarea
            ref={inputRef}
            id={inputId}
            value={value}
            onChange={(e) => {
              setValidationState(true);
              commitValue(e.target.value);
            }}
            onBlur={validate}
            name={props.fieldName}
            aria-invalid={!validationState}
            {...unlabeledAriaProps(props.placeholder, props.accessibleName)}
            rows={4}
            className={getInputClassName(
              validationState,
              "min-h-[7.5rem] resize-y",
            )}
          />
        </FieldControl>
        <FieldFooter>
          {!validationState ? (
            <span className="text-red-600" role="alert">
              Заполните поле
            </span>
          ) : props.footertext ? (
            <span className="text-slate-500">{props.footertext}</span>
          ) : null}
        </FieldFooter>
      </FieldShell>
    );
  },
);

TextAreaInput.displayName = "TextAreaInput";

export { TextAreaInput };
export type { TextAreaInputProps };
